import BaseStepHandler from './BaseStepHandler';
import SlabModal from '../../ui/SlabModal';

/**
 * Handler for slab step type with front and back side support
 * Back side now supports static content with optional QR code
 */
export default class SlabStepHandler extends BaseStepHandler {
    /**
     * Create a new SlabStepHandler
     */
    constructor(stepId, stepDefinition, canvas, stepManager, side) {
        super(stepId, stepDefinition, canvas, stepManager, side);
        this.slabModal = null;
        
        // Define QR code additional cost
        this.qrCodePrice = 5.00;
    }
    
    /**
     * Initialize slab step
     */
    initialize() {
        super.initialize();
        return this;
    }
    
    /**
     * Process template - for slab, load existing data from stepManager
     * The slab doesn't have canvas elements, but may have data loaded from edit mode
     */
    processTemplate() {
        console.log(`📦 [SLAB] processTemplate() called for side: ${this.side}, stepId: ${this.stepId}`);

        // Check if there's existing data in stepManager (loaded from edit mode)
        const existingData = this.getStepData();
        console.log('📦 [SLAB] getStepData() returned:', existingData);

        if (existingData && Object.keys(existingData).length > 0) {
            console.log('📦 [SLAB] Loading existing data from edit mode:', existingData);

            // Calculate price breakdown
            const priceBreakdown = this.getPriceBreakdown(existingData);
            existingData.priceBreakdown = priceBreakdown;

            // Update internal state
            this.currentData = existingData;

            // IMPORTANT: Store data back to stepManager to ensure it persists
            this.setStepData(existingData, false);

            // Emit event for canvas to update slab preview if enabled
            if (existingData.enabled && this.canvas) {
                console.log('📦 [SLAB] Emitting slabDataUpdate event');
                this.canvas.emit('slabDataUpdate', {
                    slabData: existingData,
                    enabled: true,
                    side: this.side
                });
            }
        } else {
            console.log('📦 [SLAB] No existing data found for edit mode');
        }

        return this;
    }
    
    /**
     * Activate slab step
     */
    activate() {
        super.activate();
        return this;
    }
    
    /**
     * Deactivate slab step
     */
    deactivate() {
        super.deactivate();
        return this;
    }
    
    /**
     * Update slab data
     * The slab doesn't appear on the canvas, we just store the data
     * 
     * @param {object} stepData - Step data
     */
    update(stepData) {

        // Calculate the price before updating
        if (stepData?.enabled) {
            const priceBreakdown = this.getPriceBreakdown(stepData);
            
            // Store price breakdown for UI display
            stepData.priceBreakdown = priceBreakdown;
        }
        
        super.update(stepData);
        
        // Emit event for canvas to update slab preview
        if (this.canvas) {
            this.canvas.emit('slabDataUpdate', { 
                slabData: stepData,
                enabled: stepData?.enabled == true,
                side: this.side
            });
        }
        
        return this;
    }

    /**
     * Get current slab data for preview
     * 
     * @returns {object} Current slab data
     */
    getSlabDataForPreview() {
        const stepData = this.getStepData();
        
        // If step data is not available or slab is not explicitly enabled, return disabled state
        // This is stricter checking to ensure we don't show slab when not explicitly enabled
        if (!stepData || stepData.enabled !== true) {
            return {
                enabled: false
            };
        }
        
        return {
            enabled: true,
            textField1: stepData.textField1 || stepData.name || '',
            textField2: stepData.textField2 || stepData.company || '',
            textField3: stepData.textField3 || stepData.date || '',
            textField4: stepData.textField4 || stepData.serial || '',
            textField5: stepData.textField5 || stepData.grade || '',
            textField6: stepData.textField6 || stepData.gradeValue || '',
            backSide: stepData.backSide || {
                qrEnabled: false,
                qrContent: '',
                qrDataUrl: null
            }
        };
    }
    
    /**
     * Get the price for this step
     * Override getPrice from BaseStepHandler to include QR pricing
     * 
     * @returns {number} Total price for the slab
     */
    getPrice() {
        const stepData = this.getStepData();
        
        // If slab is not enabled, no price
        if (!stepData || stepData.enabled === false) {
            return 0;
        }
        
        // Calculate price using breakdown
        return this.calculatePrice(stepData);
    }
    
    /**
     * Calculate the total price for the slab feature
     * Includes base price plus optional QR code price
     * 
     * @param {object} data - Slab data
     * @returns {number} - Total price
     */
    calculatePrice(data) {
        // Base slab price from step definition
        const basePrice = this.definition.price || 0;
        
        // Add QR code price if enabled
        const qrEnabled = data && data.backSide && data.backSide.qrEnabled;
        const qrPrice = qrEnabled ? this.qrCodePrice : 0;
        
        return basePrice + qrPrice;
    }
    
    /**
     * Get price breakdown for display
     * 
     * @param {object} data - Slab data
     * @returns {object} - Price breakdown
     */
    getPriceBreakdown(data) {
        const basePrice = this.definition.price || 0;
        const qrEnabled = data && data.backSide && data.backSide.qrEnabled;
        const qrPrice = qrEnabled ? this.qrCodePrice : 0;
        
        return {
            base: basePrice,
            qr: qrPrice,
            total: basePrice + qrPrice
        };
    }
    
    /**
     * Handle side change - slab doesn't have canvas elements
     */
    handleSideChange(isCurrentSide) {
        // Slab isn't rendered on canvas, so no visibility changes needed
        return this;
    }
    
    /**
     * Show slab elements - not applicable
     */
    show() {
        // Slab doesn't have canvas elements
        return this;
    }
    
    /**
     * Hide slab elements - not applicable
     */
    hide() {
        // Slab doesn't have canvas elements
        return this;
    }
    
    /**
     * Handle data received from the slab modal
     * 
     * @param {object} data - Slab data from modal
     */
    handleSlabData(data) {
        // Calculate price for display
        const priceBreakdown = this.getPriceBreakdown(data);
        
        // Set the step data to store it
        this.setStepData({
            ...data,
            enabled: true,
            priceBreakdown: priceBreakdown
            // No need to set price property as we override getPrice()
        });
        
        // Update the panel to show the new data
        this.updatePanel();
    }
    
    /**
     * Render panel content - Simplified version without preview
     */
    renderPanelContent() {
        const stepData = this.getStepData() || {};
        const basePrice = this.definition.price || 0;
        
        // Check if there's QR code data
        const hasQrData = stepData.backSide && stepData.backSide.qrEnabled && stepData.backSide.qrContent;
        
        // Calculate price breakdown
        const priceBreakdown = this.getPriceBreakdown(stepData);
        
        return `
            <div class="stc-slab-field">
                <div class="stc-slab-header">
                    <label class="stc-switch">
                        <input type="checkbox" 
                               class="stc-slab-toggle"
                               ${stepData?.enabled ? 'checked' : ''} 
                               data-price="${basePrice}">
                        <span class="stc-slider"></span>
                    </label>
                    <span class="stc-toggle-label">
                        Enable Grading Slab
                        <span class="stc-price">(+$${priceBreakdown.total.toFixed(2)})</span>
                    </span>
                </div>
                
                <div class="stc-slab-summary ${stepData?.enabled ? '' : 'stc-slab-disabled'}">
                    ${hasQrData ? 
                        `<div class="stc-slab-qr-info">
                            <div class="stc-slab-qr-summary">
                                Custom QR Code: ${stepData.backSide.qrContent}
                                <span class="stc-qr-price">(+$${this.qrCodePrice.toFixed(2)})</span>
                            </div>
                        </div>` : 
                        ''}
                </div>
                
                <button type="button" class="stc-slab-configure-btn" ${!stepData?.enabled ? 'disabled' : ''}>
                    ${stepData?.enabled ? 'Configure Slab' : 'Add Slab'}
                </button>
                
                <div class="stc-step-controls">
                    <button type="button" class="stc-clear-step" ${!stepData?.enabled ? 'disabled' : ''}>
                        Clear
                    </button>
                </div>
            </div>
        `;
    }
    
    /**
     * Bind panel events
     */
    bindPanelEvents() {
        if (!this.panelElement) return;
        
        const toggleSwitch = this.panelElement.querySelector('.stc-slab-toggle');
        const configureButton = this.panelElement.querySelector('.stc-slab-configure-btn');
        const clearButton = this.panelElement.querySelector('.stc-clear-step');
        const slabSummary = this.panelElement.querySelector('.stc-slab-summary');
        
        // Bind toggle switch
        if (toggleSwitch) {
            this.addEventListener(toggleSwitch, 'change', () => {
                const stepData = this.getStepData() || {};
                stepData.enabled = toggleSwitch.checked;
                
                // Update configure button
                if (configureButton) {
                    configureButton.disabled = !stepData.enabled;
                }
                
                // Update summary container
                if (slabSummary) {
                    slabSummary.classList.toggle('stc-slab-disabled', !stepData.enabled);
                }
                
                // Update clear button
                if (clearButton) {
                    clearButton.disabled = !stepData.enabled;
                }
                
                // Calculate updated price for display only
                const priceBreakdown = this.getPriceBreakdown(stepData);
                stepData.priceBreakdown = priceBreakdown;
                
                this.setStepData(stepData);
            });
        }
        
        // Bind configure button
        if (configureButton) {
            this.addEventListener(configureButton, 'click', () => {
                this.openSlabModal();
            });
        }
        
        // Bind clear button
        if (clearButton) {
            this.addEventListener(clearButton, 'click', () => {
                this.clear();
            });
        }
    }
    
    /**
     * Initialize and open slab modal
     */
    openSlabModal() {
        const stepData = this.getStepData() || {};
        
        if (!this.slabModal) {
            this.slabModal = new SlabModal();
            
            // Add save handler
            this.slabModal.on('save', ({ detail }) => {
                this.handleSlabData(detail);
            });
        }
        
        // Pass current data to modal
        this.slabModal.setInitialData(stepData);
        this.slabModal.show();
    }
    
    /**
     * Step-specific reset logic
     */
    onReset() {
        // When slab is reset, also update canvas preview
        if (this.canvas) {
            this.canvas.emit('slabDataUpdate', { 
                slabData: null,
                enabled: false,
                side: this.side
            });
        }
    }

    /**
     * Get export data for slab step
     * 
     * @returns {object} Export data
     */
    getStepExportData() {
        // Get base export data
        const exportData = super.getStepExportData();
        const stepData = this.getStepData();
        
        // Calculate price breakdown
        const priceBreakdown = this.getPriceBreakdown(stepData);
        
        // Add slab-specific data (for the grading slab)
        exportData.slabContent = {
            enabled: stepData?.enabled === true,
            // Price information
            price: {
                base: priceBreakdown.base,
                qr: priceBreakdown.qr,
                total: priceBreakdown.total
            },
            // Front side
            textField1: stepData?.textField1 || '',
            textField2: stepData?.textField2 || '',
            textField3: stepData?.textField3 || '',
            textField4: stepData?.textField4 || '',
            textField5: stepData?.textField5 || '',
            textField6: stepData?.textField6 || '',
            
            // Back side (now with static content and optional QR)
            backSide: {
                // Static text
                textLine1: 'CUSTOM CARD LAB',
                textLine2: '@CUSTOMCARDLAB',
                textLine3: 'ROOKIE AUTO',
                backSerial: 'CCL001',
                // Fixed logo
                hasImage: true,
                // QR code options
                qrEnabled: stepData?.backSide?.qrEnabled || false,
                qrContent: stepData?.backSide?.qrContent || '',
                qrDataUrl: stepData?.backSide?.qrDataUrl || null,
            },
            
            // The slab is a special case that gets processed during print production
            processingInstructions: {
                type: 'slab',
                placement: 'both',
                format: 'standard',
                hasFrontSide: !!(stepData?.name || stepData?.company || stepData?.serial || stepData?.grade),
                hasBackSide: true, // Always has static back content
                hasCustomQR: stepData?.backSide?.qrEnabled || false
            }
        };
        
        return exportData;
    }
    
    /**
     * Clean up resources
     */
    dispose() {
        // Clean up slab modal if it exists
        if (this.slabModal) {
            this.slabModal.off('save');
        }
        
        // Call parent dispose
        super.dispose();
    }
}